/**
 * Slice 52 — cover photo + walk order per room, kept apart from usePropertyRooms so a database
 * that has not had the new columns yet cannot break the rest of the app.
 */
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { RoomCoverRow } from "@/lib/roomCoverPhotos";

const key = (propertyId: string) => ["room-cover-data", propertyId] as const;
const CHECKED_KEY = (propertyId: string) => `stravo:hospitablePhotosChecked:${propertyId}`;

export function useRoomCoverData(propertyId: string | undefined) {
  return useQuery({
    queryKey: key(propertyId ?? ""),
    queryFn: async (): Promise<Record<string, RoomCoverRow>> => {
      if (!propertyId) return {};
      const read = async (cols: string) =>
        supabase.from("property_rooms").select(cols).eq("property_id", propertyId).is("archived_at", null);
      let res = await read("id, sort_order, cover_photo_url, cover_not_needed");
      // The "no photo needed" column may not be there yet: read what is there.
      if (res.error) res = await read("id, sort_order, cover_photo_url");
      // Columns not there yet (migration not applied): behave as "no covers, original order".
      if (res.error) return {};
      const map: Record<string, RoomCoverRow> = {};
      for (const row of (res.data ?? []) as unknown as RoomCoverRow[]) map[row.id] = row;
      return map;
    },
    enabled: !!propertyId,
  });
}

/** Update one room; if the "no photo needed" column is not there yet, save the rest. */
async function updateRoom(roomId: string, patch: Record<string, unknown>) {
  const first = await supabase.from("property_rooms").update(patch as never).eq("id", roomId);
  if (!first.error) return;
  if ("cover_not_needed" in patch) {
    const { cover_not_needed: _drop, ...rest } = patch;
    void _drop;
    if (Object.keys(rest).length === 0) throw first.error;
    const second = await supabase.from("property_rooms").update(rest as never).eq("id", roomId);
    if (!second.error) return;
    throw second.error;
  }
  throw first.error;
}

export function useSaveRoomCovers(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /** One or more rooms: roomId → photo URL. Saves right away, no confirm step. */
    mutationFn: async (covers: Record<string, string>) => {
      await Promise.all(
        Object.entries(covers).map(([roomId, url]) =>
          updateRoom(roomId, { cover_photo_url: url, cover_not_needed: false }),
        ),
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key(propertyId) }),
  });
}

/** "No photo for this room": cleaners see nothing for it. */
export function useSetRoomNoPhoto(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (roomId: string) => {
      await updateRoom(roomId, { cover_photo_url: null, cover_not_needed: true });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key(propertyId) }),
  });
}

export function useSaveWalkOrder(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderedRoomIds: string[]) => {
      const results = await Promise.all(
        orderedRoomIds.map((id, index) =>
          supabase.from("property_rooms").update({ sort_order: index } as never).eq("id", id),
        ),
      );
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
    },
    onMutate: async (orderedRoomIds) => {
      await queryClient.cancelQueries({ queryKey: key(propertyId) });
      const prev = queryClient.getQueryData<Record<string, RoomCoverRow>>(key(propertyId)) ?? {};
      const next: Record<string, RoomCoverRow> = { ...prev };
      orderedRoomIds.forEach((id, index) => {
        next[id] = {
          id,
          cover_photo_url: prev[id]?.cover_photo_url ?? null,
          cover_not_needed: prev[id]?.cover_not_needed ?? false,
          sort_order: index,
        };
      });
      queryClient.setQueryData(key(propertyId), next);
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx) queryClient.setQueryData(key(propertyId), ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key(propertyId) }),
  });
}

export function readHospitableChecked(propertyId: string): string | null {
  try {
    return localStorage.getItem(CHECKED_KEY(propertyId));
  } catch {
    return null;
  }
}

/** Ask Hospitable for the listing's photos again and save them as the property gallery (pin and name untouched). */
export function useRefreshHospitablePhotos(propertyId: string) {
  const queryClient = useQueryClient();
  const mutate = useMutation({
    mutationFn: async (): Promise<number> => {
      const { data: prop, error: propErr } = await supabase
        .from("properties")
        .select("hospitable_property_id")
        .eq("id", propertyId)
        .maybeSingle();
      if (propErr) throw propErr;
      const hid = (prop as { hospitable_property_id?: string | null } | null)?.hospitable_property_id;
      if (!hid) throw new Error("This property isn't linked to Hospitable.");
      const { data, error } = await supabase.functions.invoke("hospitable-proxy", {
        body: { action: "get_property", hospitable_property_id: hid },
      });
      if (error) throw error;
      const urls = ((data as { property?: { image_urls?: unknown } } | null)?.property?.image_urls ?? []) as unknown;
      const photoUrls = Array.isArray(urls) ? (urls as unknown[]).filter((u): u is string => typeof u === "string" && !!u) : [];
      try {
        localStorage.setItem(CHECKED_KEY(propertyId), new Date().toISOString());
      } catch {
        /* ignore */
      }
      if (photoUrls.length > 0) {
        const { error: updErr } = await supabase
          .from("properties")
          .update({ property_photo_urls: photoUrls } as never)
          .eq("id", propertyId);
        if (updErr) throw updErr;
      }
      return photoUrls.length;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["property"] });
      void queryClient.invalidateQueries({ queryKey: ["properties"] });
      void queryClient.invalidateQueries({ queryKey: ["property-with-access", propertyId] });
    },
  });
  const refresh = useCallback(() => mutate.mutateAsync(), [mutate]);
  return { refresh, isRefreshing: mutate.isPending };
}
